import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetUsersLazyQuery,
  useInviteUserMutation,
  useDeleteUserMutation,
  useUpdateUserMutation,
  UserSortEnum,
  GetUsersDocument,
  type User
} from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from './use-workspace-id';
import { isLovablePreview } from '@/lib/utils/environment';
import { activeLanguage, formatDate } from '@/lib/i18n/format';
import { formatRole } from '@/lib/utils/roles';
import { workspaceMembers } from '@/data/dummy';
import { getUserFullName } from './use-current-user';
import { toast } from 'sonner';

// Map backend User type to frontend workspace member type
export interface WorkspaceMember {
  id: string;
  name: string;
  email: string;
  lastLogin: string;
  lastLoginTs: string;
  hasLoggedIn: boolean;
  role: 'owner' | 'admin' | 'member' | 'read_only';
  profileImageUrl?: string | null;
  inviteStatus?: string | null;
  reportsTo?: string | null;
  /** Free-text job title (e.g., "VP Sales", "Account Executive"). */
  title?: string;
}

function mapUserToMember(user: User, workspaceId: string): WorkspaceMember {
  // Derive the display name via the shared helper so Teammates, People > Created By,
  // and the header all render identical names (prefers preferredName, cleans the
  // Auth0-nickname-equals-email trap, appends surname).
  const fullName = getUserFullName(user);

  // Format last login date
  const lastLogin = user.lastLoginTs
    ? formatLastLogin(user.lastLoginTs)
    : 'Never';

  // Normalize role (backend returns "user", "admin", "owner", "read_only")
  let role: 'owner' | 'admin' | 'member' | 'read_only' = 'member';
  const backendRole = (user.role as string)?.toLowerCase();
  if (backendRole === 'owner') {
    role = 'owner';
  } else if (backendRole === 'admin') {
    role = 'admin';
  } else if (backendRole === 'read_only') {
    role = 'read_only';
  } else {
    role = 'member'; // Maps "user" or anything else to "member"
  }

  return {
    id: user.id,
    name: fullName,
    email: user.email,
    lastLogin,
    lastLoginTs: user.lastLoginTs || '',
    hasLoggedIn: !!user.lastLoginTs,
    role,
    profileImageUrl: user.profileImageUrl,
    inviteStatus: user.inviteStatus,
    reportsTo: user.manager?.id ?? null,
  };
}

function formatLastLogin(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Handle future dates or negative values
  if (diffDays < 0) return 'Today';

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  // Format as "Jan 15, 2026". Module scope, so the live language is read per call.
  return formatDate(date, activeLanguage(), { month: 'short', day: 'numeric', year: 'numeric' });
}

export function useWorkspaceMembers() {
  const { t } = useTranslation();
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const workspaceId = useWorkspaceId();
  const preview = isLovablePreview();

  const [getUsersList] = useGetUsersLazyQuery();
  const [inviteUserMutation] = useInviteUserMutation();
  const [deleteUserMutation] = useDeleteUserMutation();
  const [updateUserMutation] = useUpdateUserMutation();

  const fetchMembers = useCallback(async () => {
    if (preview) {
      // Use dummy data in preview mode
      setMembers(workspaceMembers.map(m => ({
        ...m,
        role: m.role.toLowerCase() as 'owner' | 'admin' | 'member',
        lastLoginTs: m.lastLoginTs || '',
      })));
      setLoading(false);
      return;
    }

    if (!workspaceId) {
      setError('No workspace selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await getUsersList({
        variables: {
          filter: { workspaceId },
          workspaceId,
          sort: [UserSortEnum.PreferredNameAsc],
        },
      });

      if (response.data?.users?.edges) {
        const mappedMembers = response.data.users.edges
          .map(edge => edge.node)
          .filter((user): user is NonNullable<typeof user> => user !== null)
          .map(user => mapUserToMember(user as User, workspaceId));

        setMembers(mappedMembers);
      }
    } catch (err) {
      console.error('Failed to fetch workspace members:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch members');
    } finally {
      setLoading(false);
    }
  }, [getUsersList, workspaceId, preview]);

  // Fetch members on mount and when workspace changes
  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const inviteUser = useCallback(async (email: string, role: string) => {
    if (preview) {
      toast.success(t('mutations.invitationSentTo', { email, role: formatRole(role) }));
      return { success: true };
    }

    if (!workspaceId) {
      toast.error(t('mutations.noWorkspaceSelected'));
      return { success: false, error: 'No workspace selected' };
    }

    try {
      await inviteUserMutation({
        variables: {
          input: {
            email,
            role,
            workspaceId,
          },
        },
        refetchQueries: [
          {
            query: GetUsersDocument,
            variables: {
              filter: { workspaceId },
              workspaceId,
              sort: [UserSortEnum.PreferredNameAsc],
            },
          },
        ],
        awaitRefetchQueries: true, // Wait for refetch to complete
      });

      toast.success(t('mutations.invitationSentTo', { email, role: formatRole(role) }));

      // Explicitly refetch to ensure UI updates
      await fetchMembers();

      return { success: true };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('mutations.userInviteFailed');
      console.error('Failed to invite user:', err);
      toast.error(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, [inviteUserMutation, workspaceId, preview, fetchMembers, t]);

  const removeUser = useCallback(async (userId: string) => {
    if (preview) {
      setMembers(prev => prev.filter(m => m.id !== userId));
      const user = members.find(m => m.id === userId);
      toast.success(t('mutations.userRemovedFromWorkspace', { name: user?.name }));
      return { success: true };
    }

    if (!workspaceId) {
      toast.error(t('mutations.noWorkspaceSelected'));
      return { success: false, error: 'No workspace selected' };
    }

    try {
      const response = await deleteUserMutation({
        variables: {
          input: {
            userId: parseInt(userId),
            workspaceId: parseInt(workspaceId),
          },
        },
        refetchQueries: [
          {
            query: GetUsersDocument,
            variables: {
              filter: { workspaceId },
              workspaceId,
              sort: [UserSortEnum.PreferredNameAsc],
            },
          },
        ],
        awaitRefetchQueries: true, // Wait for refetch to complete
      });

      const user = members.find(m => m.id === userId);

      if (response.data?.deleteUserRole?.success) {
        toast.success(t('mutations.userRemovedFromWorkspace', { name: user?.name }));

        // Explicitly refetch to ensure UI updates
        await fetchMembers();

        return { success: true };
      } else {
        const error = response.data?.deleteUserRole?.error || t('mutations.userRemoveFailed');
        toast.error(error);
        return { success: false, error };
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('mutations.userRemoveFailed');
      console.error('Failed to remove user:', err);
      toast.error(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, [deleteUserMutation, workspaceId, members, preview, fetchMembers, t]);

  const changeRole = useCallback(async (userId: string, newRole: string) => {
    if (preview) {
      setMembers(prev => prev.map(m => m.id === userId ? { ...m, role: newRole as 'owner' | 'admin' | 'member' } : m));
      toast.success(t('mutations.roleUpdated'));
      return { success: true };
    }

    if (!workspaceId) {
      toast.error(t('mutations.noWorkspaceSelected'));
      return { success: false, error: 'No workspace selected' };
    }

    try {
      await updateUserMutation({
        variables: {
          input: {
            id: parseInt(userId),
            workspaceRole: {
              role: newRole,
              workspaceId: parseInt(workspaceId),
            },
            workspaceId,
          },
          workspaceId,
        },
        refetchQueries: [
          {
            query: GetUsersDocument,
            variables: {
              filter: { workspaceId },
              workspaceId,
              sort: [UserSortEnum.PreferredNameAsc],
            },
          },
        ],
        awaitRefetchQueries: true, // Wait for refetch to complete
      });

      toast.success(t('mutations.roleUpdated'));

      // Explicitly refetch to ensure UI updates
      await fetchMembers();

      return { success: true };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('mutations.roleUpdateFailed');
      console.error('Failed to update role:', err);
      toast.error(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, [updateUserMutation, workspaceId, preview, fetchMembers, t]);

  return {
    members,
    loading,
    error,
    inviteUser,
    removeUser,
    changeRole,
    refetch: fetchMembers,
  };
}
